/**
 * Track Types
 *
 * Truthful definitions:
 * - Track Cell = (item_category × altitude_level). The conceptual Track (comparability lane).
 * - The DB table `tracks` represents a Track Instance: Track Cell contextualized by (cycle_id × scope).
 *   Cycle and scope are context, not the essence of Track.
 *
 * Legacy: Track/TrackRow remain the domain and DB shape. TrackCell and TrackInstance are adapters
 * so new code can use Track Cell as the semantic core.
 */

export type TrackId = string;

// ============================================================================
// Track Cell (conceptual Track = item_category × altitude_level)
// ============================================================================

/**
 * Track Cell: the conceptual comparability lane.
 * No cycle or scope — just the (item_category × altitude_level) dimensions.
 */
export interface TrackCell {
  itemCategory: string;
  altitudeLevel: number;
}

/**
 * Context that turns a Track Cell into a Track Instance (when + where).
 * cycleId is optional for standalone tracks (is_standalone = true).
 */
export interface TrackInstanceContext {
  cycleId: string | null;
  scopeType: string;
  scopeEntityId: string | null;
  scopeLabel: string;
  /** True when this track exists outside any DEGAP cycle. */
  isStandalone?: boolean;
}

/**
 * Track Instance: a Track Cell contextualized by (cycle_id × scope).
 * Adapter over the existing Track type — use this when logic should reason about
 * "Track Cell as semantic core" and context separately.
 */
export interface TrackInstance {
  id: TrackId;
  trackCell: TrackCell;
  context: TrackInstanceContext;
  name: string;
  description: string | null;
  status: TrackStatus;
  metadata: Record<string, unknown> | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Build TrackCell from a Track (or TrackRow).
 */
export function trackToTrackCell(track: { requiredItemCategory: string; requiredAltitudeLevel: number }): TrackCell {
  return {
    itemCategory: track.requiredItemCategory,
    altitudeLevel: track.requiredAltitudeLevel,
  };
}

/**
 * Build TrackInstanceContext from a Track (or TrackRow).
 */
export function trackToContext(track: {
  cycleId: string | null;
  scopeType: string;
  scopeEntityId: string | null;
  scopeLabel: string;
  isStandalone?: boolean;
}): TrackInstanceContext {
  return {
    cycleId: track.cycleId,
    scopeType: track.scopeType,
    scopeEntityId: track.scopeEntityId,
    scopeLabel: track.scopeLabel,
    isStandalone: track.isStandalone ?? false,
  };
}

/**
 * Adapter: existing Track (DB-backed) -> TrackInstance (Track Cell + context).
 */
export function trackToTrackInstance(track: Track): TrackInstance {
  return {
    id: track.id,
    trackCell: trackToTrackCell(track),
    context: trackToContext(track),
    name: track.name,
    description: track.description,
    status: track.status,
    metadata: track.metadata,
    createdAt: track.createdAt,
    updatedAt: track.updatedAt,
  };
}

/**
 * Compare two TrackCells for equality (same comparability lane).
 */
export function trackCellsEqual(a: TrackCell, b: TrackCell): boolean {
  return a.itemCategory === b.itemCategory && a.altitudeLevel === b.altitudeLevel;
}

/**
 * Resolve a canonical key for a Track Cell (category × altitude).
 * Use for leveling map keys and cross-track comparison; same key = same comparability lane.
 */
export function resolveCanonical(cell: TrackCell): string {
  return `${String(cell.itemCategory).trim().toLowerCase()}:${cell.altitudeLevel}`;
}

/** Resolve canonical key from raw category and altitude (e.g. from item or config). */
export function resolveCanonicalFromCategoryAltitude(itemCategory: string, altitudeLevel: number): string {
  return resolveCanonical({ itemCategory, altitudeLevel });
}

// ============================================================================
// Track (legacy domain object = Track Instance; DB table `tracks`)
// ============================================================================

/**
 * Track scope types
 * - 'default': The single default track per cycle (no filtering)
 * - 'org_unit': Scoped to an organizational unit
 * - 'custom_group': Custom grouping defined by the user
 */
export type TrackScopeType = 'default' | 'org_unit' | 'custom_group';

/**
 * Track status values
 * - 'active': Track is in use
 * - 'accepted': Track's final proposal was accepted
 * - 'final_rejected': Track's proposal was finally rejected
 * - 'archived': Track is no longer active
 */
export type TrackStatus = 'active' | 'accepted' | 'final_rejected' | 'archived';

/** The word for each status, wherever a track's status is shown. */
export const TRACK_STATUS_LABEL: Record<TrackStatus, string> = {
  active: 'Active',
  accepted: 'Accepted',
  final_rejected: 'Rejected',
  archived: 'Archived',
};

/**
 * Track database row (snake_case, matches SUPABASE_SCHEMA_LIVE.md)
 */
export interface TrackRow {
  id: string;
  /** NULL for standalone tracks (is_standalone = true). */
  cycle_id: string | null;
  scope_type: string;
  scope_entity_id: string | null;
  scope_label: string;
  required_item_category: string;
  required_altitude_level: number;
  name: string;
  description: string | null;
  status: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // Track-native lock states (v3.0)
  // Phase C-1 (canon 28 §6, migration 596): criteria_locked_at and
  // values_converged_at moved to session_baselines (embedded on fetch).
  participation_locked_at: string | null;
  range_set_locked_at?: string | null;
  // Session defaults: Phase C-2 (canon 28 §6, migration 598) — the
  // default_timing_mode / default_entry_mode / default_facilitator_id columns
  // were dropped; policy lives on session_policies ('track' scope) and is
  // decorated onto the domain object after fetch.
  official_session_id?: string | null;
  /** Canonical Official Track Result pointer (session_reports.id). */
  official_session_report_id?: string | null;
  /** Track-level result resolution override; only when workflow allows. */
  result_resolution_override?: string | null;
  /** True when this track exists outside any DEGAP cycle. */
  is_standalone?: boolean;
  /** Workspace this track belongs to. Required for standalone tracks; cycle-linked tracks inherit scope from their parent cycle. */
  workspace_id?: string | null;
  /**
   * FK to org_units, set only when scope_type = 'org_unit' and
   * scope_entity_id is that org unit's id. This is the column the
   * reorganization worklist and mig-729 integrity checks read — a track
   * scoped to an org unit only via scope_entity_id (and not org_unit_id) is
   * invisible to a reorg.
   */
  org_unit_id?: string | null;
}

/**
 * Track domain object (camelCase, for TypeScript use)
 */
export interface Track {
  id: TrackId;
  /** NULL for standalone tracks (is_standalone = true). */
  cycleId: string | null;
  scopeType: TrackScopeType;
  scopeEntityId: string | null;
  scopeLabel: string;
  requiredItemCategory: string;
  requiredAltitudeLevel: number;
  name: string;
  description: string | null;
  status: TrackStatus;
  metadata: Record<string, unknown> | null;
  createdAt: Date;
  updatedAt: Date;
  // Track-native lock states (v3.0)
  participationLockedAt: Date | null;
  criteriaLockedAt: Date | null;
  rangeSetLockedAt: Date | null;
  valuesConvergedAt: Date | null;
  // Session defaults (inheritance: workspace → cycle → track → session).
  // Phase C-2 (migration 598): populated by decoration from the
  // session_policies 'track' scope row, not from tracks columns.
  defaultTimingMode: 'live' | 'async' | null;
  defaultEntryMode: 'facilitator' | 'participants' | null;
  defaultFacilitatorId: string | null;
  /** Stage 5: When result_resolution is selected_official, the session that is the Official Track Result. */
  officialSessionId: string | null;
  /** Canonical Official Track Result: FK to session_reports.id. */
  officialSessionReportId: string | null;
  /** Track-level result resolution override; only when workflow allow_track_resolution_override is true. */
  resultResolutionOverride: ResultResolutionOverride | null;
  /** Readiness definition override for this track — null means inherit from cycle/workspace. */
  readinessDefinitionId: string | null;
  /** True when this track exists outside any DEGAP cycle. */
  isStandalone: boolean;
  /** Workspace this track belongs to. Set on creation for standalone tracks; null for legacy cycle-linked tracks before migration. */
  workspaceId: string | null;
  // Brief E: Value Collection selection
  valueCollectionId: string | null;
  valueCollectionSnapshotId: string | null;
  valueSourceSelectedBy: string | null;
  valueSourceSelectedAt: Date | null;
  /** Last Pool Amendment on this track's locked pool (session_baselines, 723; set by amend_locked_pool). */
  poolAmendedAt?: Date | null;
  /** The reason recorded with that amendment. */
  poolAmendmentNote?: string | null;
}

/** Valid values for tracks.result_resolution_override. */
export type ResultResolutionOverride = 'latest_published' | 'aggregate' | 'selected_official';

/**
 * Persisted on `tracks.metadata` — accountable person for the track.
 * Distinct from participation / working tier (sessions, Catchball routing).
 */
export const TRACK_OWNER_USER_ID_METADATA_KEY = 'trackOwnerUserId' as const;

export function getTrackOwnerUserIdFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const v = metadata?.[TRACK_OWNER_USER_ID_METADATA_KEY];
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

/** Merge or clear track owner in metadata; preserves unrelated keys. */
export function mergeTrackOwnerIntoMetadata(
  existing: Record<string, unknown> | null | undefined,
  ownerUserId: string | null,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(existing ?? {}) };
  if (ownerUserId && ownerUserId.trim().length > 0) {
    next[TRACK_OWNER_USER_ID_METADATA_KEY] = ownerUserId.trim();
  } else {
    delete next[TRACK_OWNER_USER_ID_METADATA_KEY];
  }
  return next;
}

/**
 * Key under which a multi-cell track cell list is stored in tracks.metadata.
 * Shape: Array<{ itemCategory: string; altitudeLevel: number }>
 * Set when a track covers more than one (category × altitude) cell.
 * When present, item eligibility should be checked against this list instead of
 * the single requiredItemCategory / requiredAltitudeLevel fields.
 */
export const TRACK_CELLS_METADATA_KEY = 'trackCells' as const;

export interface TrackCellEntry {
  itemCategory: string;
  altitudeLevel: number;
}

export function getTrackCellsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): TrackCellEntry[] | null {
  const v = metadata?.[TRACK_CELLS_METADATA_KEY];
  if (!Array.isArray(v)) return null;
  return v.filter(
    (c): c is TrackCellEntry =>
      c !== null &&
      typeof c === 'object' &&
      typeof (c as TrackCellEntry).itemCategory === 'string' &&
      typeof (c as TrackCellEntry).altitudeLevel === 'number',
  );
}

/** Merge a multi-cell selection into metadata; preserves unrelated keys. */
export function mergeTrackCellsIntoMetadata(
  existing: Record<string, unknown> | null | undefined,
  cells: TrackCellEntry[],
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(existing ?? {}) };
  if (cells.length > 0) {
    next[TRACK_CELLS_METADATA_KEY] = cells;
  } else {
    delete next[TRACK_CELLS_METADATA_KEY];
  }
  return next;
}

/**
 * Session Policy flags formerly persisted on tracks.metadata.
 * Phase C-2 (canon 28 §6, migration 598): these live on session_policies
 * ('track' scope) and are synthesized back into the domain metadata by
 * decoration. Strip them before any tracks.metadata write so decorated
 * values are never re-persisted to the retired surface.
 */
export const SESSION_POLICY_TRACK_METADATA_KEYS = [
  'allowSessionOverride',
  'allowSessionItemCreation',
  'allowSessionValueCreation',
  'allowSessionValueOverride',
] as const;

export function stripSessionPolicyKeysFromTrackMetadata(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (metadata == null) return metadata ?? null;
  const next = { ...metadata };
  for (const key of SESSION_POLICY_TRACK_METADATA_KEYS) {
    delete next[key];
  }
  return next;
}

/**
 * Input for creating a new track.
 * For cycle-linked tracks, cycleId is required.
 * For standalone tracks, omit cycleId and set isStandalone: true.
 */
export interface CreateTrackInput {
  cycleId?: string | null;
  name: string;
  description?: string;
  scopeType?: TrackScopeType;
  scopeEntityId?: string | null;
  scopeLabel?: string;
  requiredItemCategory?: string;
  requiredAltitudeLevel?: number;
  /** Persisted as tracks.metadata.trackOwnerUserId; triggers in-app notification when set. */
  ownerUserId?: string | null;
  /** Set to true to create a standalone track (no parent cycle). */
  isStandalone?: boolean;
  /** Additional metadata to merge into tracks.metadata at creation time. */
  metadata?: Record<string, unknown>;
}

/**
 * Input for updating an existing track
 */
export interface UpdateTrackInput {
  name?: string;
  description?: string | null;
  scopeLabel?: string;
  status?: TrackStatus;
  metadata?: Record<string, unknown> | null;
  requiredItemCategory?: string;
  requiredAltitudeLevel?: number;
  // Track-native lock states (v3.0)
  // Phase C-1: criteriaLockedAt / valuesConvergedAt are Baseline content —
  // written via set_track_criteria_locked / set_track_values_converged /
  // lock_track_values RPCs, never through updateTrack.
  participationLockedAt?: Date | null;
  // Session defaults: Phase C-2 — defaultTimingMode / defaultEntryMode /
  // defaultFacilitatorId are Policy content, written via
  // sessionPolicyStore.upsertPolicy ('track' scope), never through updateTrack.
  /** Readiness definition override — null means inherit from cycle/workspace. */
  readinessDefinitionId?: string | null;
  /** Stage 5: Set which session is the Official Track Result (selected_official only). */
  officialSessionId?: string | null;
  /** Canonical Official Track Result: session_reports.id. */
  officialSessionReportId?: string | null;
  /** Track-level result resolution override (only when workflow allows). */
  resultResolutionOverride?: ResultResolutionOverride | null;
}

/**
 * Convert database row to domain object
 */
export function trackRowToDomain(row: TrackRow): Track {
  // Phase C-1 (canon 28 §6, migration 596): Baseline content lives only on
  // session_baselines. Fetches must embed the Baseline row; there is no
  // tracks-column fallback anymore (the mirrored columns were dropped).
  const rawBaseline = (row as unknown as { session_baselines?: unknown }).session_baselines;
  const baseline = (Array.isArray(rawBaseline) ? rawBaseline[0] : rawBaseline) as
    | {
        criteria_locked_at?: string | null;
        values_converged_at?: string | null;
        value_collection_id?: string | null;
        value_collection_snapshot_id?: string | null;
        value_source_selected_by?: string | null;
        value_source_selected_at?: string | null;
        pool_amended_at?: string | null;
        pool_amendment_note?: string | null;
      }
    | undefined;
  return {
    id: row.id,
    cycleId: row.cycle_id,
    scopeType: row.scope_type as TrackScopeType,
    scopeEntityId: row.scope_entity_id,
    scopeLabel: row.scope_label,
    requiredItemCategory: row.required_item_category,
    requiredAltitudeLevel: row.required_altitude_level,
    name: row.name,
    description: row.description,
    status: row.status as TrackStatus,
    metadata: row.metadata,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    participationLockedAt: row.participation_locked_at ? new Date(row.participation_locked_at) : null,
    criteriaLockedAt: baseline?.criteria_locked_at ? new Date(baseline.criteria_locked_at) : null,
    rangeSetLockedAt: row.range_set_locked_at ? new Date(row.range_set_locked_at) : null,
    valuesConvergedAt: baseline?.values_converged_at ? new Date(baseline.values_converged_at) : null,
    // Phase C-2 (598): Policy content lives on session_policies; the store
    // decorates these after fetch (decorateTrackWithPolicy).
    defaultTimingMode: null,
    defaultEntryMode: null,
    defaultFacilitatorId: null,
    officialSessionId: row.official_session_id ?? null,
    officialSessionReportId: row.official_session_report_id ?? null,
    resultResolutionOverride: (row.result_resolution_override === 'latest_published' || row.result_resolution_override === 'aggregate' || row.result_resolution_override === 'selected_official')
      ? row.result_resolution_override
      : null,
    readinessDefinitionId: row.readiness_definition_id ?? null,
    isStandalone: row.is_standalone ?? false,
    workspaceId: row.workspace_id ?? null,
    valueCollectionId: baseline?.value_collection_id ?? null,
    valueCollectionSnapshotId: baseline?.value_collection_snapshot_id ?? null,
    valueSourceSelectedBy: baseline?.value_source_selected_by ?? null,
    valueSourceSelectedAt: baseline?.value_source_selected_at ? new Date(baseline.value_source_selected_at) : null,
    poolAmendedAt: baseline?.pool_amended_at ? new Date(baseline.pool_amended_at) : null,
    poolAmendmentNote: baseline?.pool_amendment_note ?? null,
  };
}

/**
 * Convert domain object to database row for insert/update.
 * cycleId is optional to support standalone tracks.
 */
export function trackDomainToRow(track: Partial<Track> & { cycleId?: string | null }): Partial<TrackRow> {
  const row: Partial<TrackRow> = {
    cycle_id: track.cycleId ?? null,
    is_standalone: track.isStandalone ?? false,
  };

  if (track.id !== undefined) row.id = track.id;
  if (track.scopeType !== undefined) row.scope_type = track.scopeType;
  if (track.scopeEntityId !== undefined) row.scope_entity_id = track.scopeEntityId;
  if (track.scopeLabel !== undefined) row.scope_label = track.scopeLabel;
  if (track.requiredItemCategory !== undefined) row.required_item_category = track.requiredItemCategory;
  if (track.requiredAltitudeLevel !== undefined) row.required_altitude_level = track.requiredAltitudeLevel;
  if (track.name !== undefined) row.name = track.name;
  if (track.description !== undefined) row.description = track.description;
  if (track.status !== undefined) row.status = track.status;
  if (track.metadata !== undefined) row.metadata = stripSessionPolicyKeysFromTrackMetadata(track.metadata);
  // Phase C-2 (598): defaultTimingMode / defaultEntryMode / defaultFacilitatorId
  // are Policy content — persisted via session_policies, never emitted here.
  if (track.officialSessionId !== undefined) row.official_session_id = track.officialSessionId === null || track.officialSessionId === '' ? null : track.officialSessionId;
  if (track.officialSessionReportId !== undefined) row.official_session_report_id = track.officialSessionReportId === null || track.officialSessionReportId === '' ? null : track.officialSessionReportId;
  if (track.resultResolutionOverride !== undefined) row.result_resolution_override = track.resultResolutionOverride === null || track.resultResolutionOverride === '' ? null : track.resultResolutionOverride;
  if (track.readinessDefinitionId !== undefined) row.readiness_definition_id = track.readinessDefinitionId ?? null;
  if (track.isStandalone !== undefined) row.is_standalone = track.isStandalone;
  if (track.workspaceId !== undefined) row.workspace_id = track.workspaceId;

  return row;
}

/**
 * Special constants
 */
export const UNSCOPED_CATEGORY = 'UNSCOPED';
export const UNSCOPED_ALTITUDE = -1;

/**
 * Check if a track is the default track (unscoped)
 */
export function isDefaultTrack(track: Track): boolean {
  return (
    track.scopeType === 'default' &&
    track.requiredItemCategory === UNSCOPED_CATEGORY &&
    track.requiredAltitudeLevel === UNSCOPED_ALTITUDE
  );
}

/**
 * Get a human-readable label for a track's cell (Category × Altitude)
 */
export function getTrackCellLabel(track: Track): string {
  const category = track.requiredItemCategory === UNSCOPED_CATEGORY
    ? 'Any Category'
    : track.requiredItemCategory;
    
  const altitude = track.requiredAltitudeLevel === UNSCOPED_ALTITUDE
    ? 'Any Altitude'
    : `Level ${track.requiredAltitudeLevel}`;
    
  return `${category} × ${altitude}`;
}
